import type { Address } from "viem"
import type { Config } from "wagmi"
import { formatUnits, fromHex } from "viem"

import type { ApprovalMode, ContractRiskLevel, EmitEvent, Token } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { CreateLockLensResult } from "@/domains/on-chain"
import { determineApprovalAmount, EventEmitter, makeApprovalDescription, makeGroupId } from "@/domains/library"
import { LockCreatedEvent } from "@/domains/lock/events"
import { BIGINT_ZERO } from "@/domains/on-chain"
import { TokenApprovalCompletedEvent } from "@/domains/tokens/events"

import type { ICreateLockActions } from "./interfaces"

interface CreateLockInteractionParams {
  account: Address
  chainId: number
  config: Config
  amount: bigint
  formattedLockDate: string
  secondsToExpire: number
  createLockLens: CreateLockLensResult
  governanceToken: Token
  vestedToken: Token
  approvalMode: ApprovalMode
  contractRiskLevel: ContractRiskLevel
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
  onChainActions: ICreateLockActions
}

export const createLockInteraction = async ({
  account,
  chainId,
  config,
  amount,
  formattedLockDate,
  secondsToExpire,
  createLockLens,
  governanceToken,
  vestedToken,
  approvalMode,
  contractRiskLevel,
  emitter,
  eventBus,
  onChainActions,
}: CreateLockInteractionParams) => {
  // Generate a unique group ID for this interaction instance
  const groupId = makeGroupId("create-lock")
  // Do we have a value to lock?
  if (amount <= BIGINT_ZERO) {
    emitter.emit("error-creating-lock", {
      title: "Create lock failed",
      description: "You must enter a value greater than 0",
      variant: "info",
    })
    return false
  }

  // does the user have enough funds to make the lock?
  const balance = createLockLens.accountBalanceToken.wei ?? BIGINT_ZERO
  if (balance < amount) {
    emitter.emit("error-creating-lock", {
      title: "Create lock failed",
      description: `Insufficient funds`,
      variant: "info",
    })
    return false
  }

  // does the user have enough allowance to lock?
  const allowance = createLockLens.vestedSpendTokenAllowance.wei ?? BIGINT_ZERO
  if (allowance < amount) {
    const actualAmountToApprove = determineApprovalAmount(amount, approvalMode, contractRiskLevel)
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: makeApprovalDescription(actualAmountToApprove, vestedToken.decimals),
      variant: "info",
    })

    const approval = await onChainActions.approveTokenAllowance({
      account,
      chainId,
      config,
      contractAddress: governanceToken.address,
      spenderAddress: vestedToken.address,
      spenderAmount: amount,
      approvalMode,
      contractRiskLevel,
    })

    if (approval.success) {
      eventBus.publish(
        new TokenApprovalCompletedEvent(groupId, governanceToken.address, vestedToken.address, amount, approval.tx),
      )
      emitter.emit("approve-token-allowance", { title: "Approval successful", variant: "success" })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error,
        variant: "error",
      })
      return false
    }
  }

  emitter.emit("creating-lock", {
    title: "Create lock",
    description: `Locking ${formatUnits(amount, governanceToken.decimals)} ${governanceToken.symbol} until ${formattedLockDate}`,
    variant: "info",
  })

  const result = await onChainActions.createLock({
    account,
    chainId,
    config,
    vestedToken,
    amount,
    secondsToExpire,
  })

  // we should get the created token ID (from somewhere???)

  if (result.success) {
    // the token ID will be in the result
    const tokenId = fromHex(result.transaction?.logs[0]?.topics[3] ?? "0x0", "number")

    eventBus.publish(new LockCreatedEvent(groupId, tokenId, amount, secondsToExpire, result.tx))

    emitter.emit("create-lock", {
      title: "Create lock",
      description: `NFT #${tokenId} created with ${formatUnits(amount, governanceToken.decimals)} locked ${governanceToken.symbol} until ${formattedLockDate}`,
      variant: "success",
    })
  }

  if (result.success === false) {
    emitter.emit("error-creating-lock", {
      title: "Create lock failed",
      description: result.error,
      variant: "error",
    })
  }

  return result
}
